import { cn } from "../lib/cn.js";

/** A donut from stroke-dashoffset arcs — no chart library, no CSP concerns. */
export function Donut({
  segments,
  size = 132,
}: {
  readonly segments: readonly { readonly label: string; readonly value: number; readonly className: string }[];
  readonly size?: number;
}): React.JSX.Element {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const stroke = 16;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size} viewBox={`0 0 ${String(size)} ${String(size)}`} className="shrink-0 -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="stroke-muted" />
        {total > 0 &&
          segments.map((s) => {
            const len = (s.value / total) * c;
            const dash = <circle key={s.label} cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} strokeDasharray={`${String(len)} ${String(c - len)}`} strokeDashoffset={-offset} className={s.className} />;
            offset += len;
            return dash;
          })}
      </svg>
      <ul className="flex flex-col gap-1.5 text-sm">
        {segments.map((s) => (
          <li key={s.label} className="flex items-center gap-2">
            <span className={cn("inline-block size-2.5 rounded-full", s.className.replace("stroke-", "bg-"))} />
            <span className="text-muted-foreground">{s.label}</span>
            <span className="ml-auto font-medium tabular-nums">{s.value.toLocaleString()}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Horizontal bars scaled to the max — for plan mix, onboarding funnel, new stores. */
export function BarList({
  items,
  barClassName = "bg-primary",
}: {
  readonly items: readonly { readonly label: string; readonly value: number }[];
  readonly barClassName?: string;
}): React.JSX.Element {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <ul className="flex flex-col gap-2">
      {items.map((item) => (
        <li key={item.label} className="grid grid-cols-[8rem_1fr_auto] items-center gap-2 text-sm">
          <span className="text-muted-foreground truncate">{item.label}</span>
          <span className="bg-muted h-2.5 overflow-hidden rounded-full">
            <span className={cn("block h-full rounded-full", barClassName)} style={{ width: `${String(Math.round((item.value / max) * 100))}%` }} />
          </span>
          <span className="w-10 text-right font-medium tabular-nums">{item.value.toLocaleString()}</span>
        </li>
      ))}
    </ul>
  );
}

/** A two-series area/line for the per-day counts. Purely presentational SVG. */
export function DualLine({
  a,
  b,
  width = 520,
  height = 120,
}: {
  readonly a: { readonly label: string; readonly points: readonly number[]; readonly className: string };
  readonly b: { readonly label: string; readonly points: readonly number[]; readonly className: string };
  readonly width?: number;
  readonly height?: number;
}): React.JSX.Element {
  const n = Math.max(a.points.length, b.points.length);
  const max = Math.max(1, ...a.points, ...b.points);
  const path = (pts: readonly number[]): string =>
    pts.length === 0
      ? ""
      : pts
          .map((v, i) => {
            const x = n <= 1 ? 0 : (i / (n - 1)) * width;
            const y = height - (v / max) * (height - 8) - 4;
            return `${i === 0 ? "M" : "L"}${String(Math.round(x))},${String(Math.round(y))}`;
          })
          .join(" ");
  return (
    <div className="flex flex-col gap-2">
      <svg width="100%" viewBox={`0 0 ${String(width)} ${String(height)}`} className="w-full" preserveAspectRatio="none">
        <polyline points="" />
        <path d={path(a.points)} fill="none" strokeWidth={2} className={a.className} />
        <path d={path(b.points)} fill="none" strokeWidth={2} className={b.className} />
      </svg>
      <div className="text-muted-foreground flex gap-4 text-xs">
        <span className="flex items-center gap-1.5"><span className={cn("inline-block h-0.5 w-3", a.className.replace("stroke-", "bg-"))} />{a.label}</span>
        <span className="flex items-center gap-1.5"><span className={cn("inline-block h-0.5 w-3", b.className.replace("stroke-", "bg-"))} />{b.label}</span>
      </div>
    </div>
  );
}
